import { useEffect, useState, useCallback } from "react";
import { FileSignature, Plus, Trash2, Pencil, Send, Eye, Loader2, CheckCircle2, Search, LayoutTemplate, Users, PenLine } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { FormBuilder } from "@/components/forms/FormBuilder";
import { FormRenderer } from "@/components/forms/FormRenderer";
import { newField, SECOND_SIGNER_OPTIONS, SECOND_SIGNER_LABEL } from "@/components/forms/fieldTypes";
import { FileUploader, FileChip } from "@/components/forms/FormFiles";
import { FormSendActions, SEND_STATUS } from "@/components/forms/FormSendActions";
import { useAuth } from "@/context/AuthContext";

const inputCls = "w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none";
const emptyForm = { title: "", description: "", fields: [], assigned_school_ids: [], attachments: [], second_signer: "" };

const SUBTITLE = {
  corporate: "District forms are available to every school in the district.",
  admin: "Create forms for this school and send them to parents to fill and e-sign. District forms appear here too.",
  officer: "Create forms for this school and send them to parents to fill and e-sign.",
  teacher: "Create forms for your class, or send school and district forms to the parents of your students.",
};

export const FormsManager = ({ isCorporate = false, mode }) => {
  const { user } = useAuth();
  const role = mode || (isCorporate ? "corporate" : "admin");
  const canSend = role !== "corporate";
  const [forms, setForms] = useState([]);
  const [editor, setEditor] = useState(null);
  const [preview, setPreview] = useState(false);
  const [previewAnswers, setPreviewAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [sendFor, setSendFor] = useState(null);
  const [respFor, setRespFor] = useState(null);
  const [responses, setResponses] = useState([]);
  const [counterFor, setCounterFor] = useState(null);
  const [counterName, setCounterName] = useState("");
  const [countersigning, setCountersigning] = useState(false);

  const countersign = async () => {
    if (counterName.trim().length < 2) { toast.error("Type your full name to countersign."); return; }
    setCountersigning(true);
    try {
      await api.post(`/forms/sends/${counterFor.id}/countersign`, { signature_name: counterName.trim() });
      toast.success("Countersigned — form is fully signed.");
      setCounterFor(null); setCounterName("");
      const { data } = await api.get(`/forms/${respFor.id}/responses`); setResponses(data); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Countersign failed"); }
    finally { setCountersigning(false); }
  };

  const load = useCallback(() => {
    api.get("/forms").then(({ data }) => setForms(data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load forms"));
  }, [role]);

  useEffect(load, [load]);

  const saveForm = async () => {
    if (!editor.title.trim()) { toast.error("Give the form a title."); return; }
    if (editor.fields.length === 0) { toast.error("Add at least one field."); return; }
    if (editor.fields.some((f) => !f.label.trim())) { toast.error("Every field needs a label."); return; }
    setSaving(true);
    try {
      if (editor.id) await api.put(`/forms/${editor.id}`, editor);
      else await api.post("/forms", editor);
      toast.success(editor.id ? "Form updated" : "Form created");
      setEditor(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const deleteForm = async (f) => {
    if (!window.confirm(`Delete "${f.title}"? Pending (unsigned) sends are removed; signed copies are kept.`)) return;
    try {
      await api.delete(`/forms/${f.id}`);
      toast.success("Form deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); }
  };

  const openResponses = async (f) => {
    setRespFor(f);
    try {
      const { data } = await api.get(`/forms/${f.id}/responses`);
      setResponses(data);
    } catch { setResponses([]); }
  };


  const openEditor = (f) => { setPreview(false); setPreviewAnswers({}); setEditor(f); };

  return (
    <section data-testid="forms-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <FileSignature className="h-6 w-6 text-primary" /> Forms
          </h1>
          <div className="text-xs text-slate-500">{SUBTITLE[role]}</div>
        </div>
        {(
          <button onClick={() => openEditor({ ...emptyForm, fields: [newField("text")] })} data-testid="forms-create-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:shadow transition">
            <Plus className="h-4 w-4" /> New form
          </button>
        )}
      </div>

      <div className="grid gap-3">
        {forms.length === 0 && <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">No forms yet — create your first one.</div>}
        {forms.map((f) => {
          const mine = role === "corporate" ? f.owner_type === "corporate"
            : role === "teacher" ? f.creator_id === user?.id
            : f.owner_type === "school";
          return (
            <div key={f.id} className="rounded-xl border border-slate-200 bg-white px-5 py-4 flex flex-wrap items-center gap-3" data-testid={`form-row-${f.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900 flex items-center gap-2">
                  {f.title}
                  {f.owner_type === "corporate" && (
                    <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider" data-testid={`form-badge-district-${f.id}`}>District</span>
                  )}
                  {f.scope === "class" && (
                    <span className="rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider" data-testid={`form-badge-class-${f.id}`}>Class</span>
                  )}
                  {f.second_signer && (
                    <span className="rounded-full bg-indigo-100 text-indigo-800 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider inline-flex items-center gap-1" title={SECOND_SIGNER_LABEL[f.second_signer]} data-testid={`form-badge-2sig-${f.id}`}><PenLine className="h-3 w-3" /> 2 signatures</span>
                  )}
                </div>
                <div className="text-xs text-slate-500">
                  {f.fields.length} field{f.fields.length === 1 ? "" : "s"}{(f.attachments || []).length ? ` · ${f.attachments.length} attachment${f.attachments.length === 1 ? "" : "s"}` : ""} · {f.completed_count}/{f.sent_count} signed{f.awaiting_count ? ` · ${f.awaiting_count} awaiting 2nd` : ""}
                  {role === "corporate" && " · all schools"}
                  {" · by "}{f.owner_name}
                  {f.version_label && <span className="ml-2 rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-600" title={f.updated_at ? `Last edited ${new Date(f.updated_at).toLocaleDateString()} by ${f.updated_by || ""}` : "Version"} data-testid={`form-version-${f.id}`}>{f.version_label}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {canSend && (
                  <>
                    <button onClick={() => setSendFor(f)} data-testid={`form-send-${f.id}`} title="Send to parents"
                      className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 transition">
                      <Send className="h-3.5 w-3.5" /> Send
                    </button>
                    <button onClick={() => openResponses(f)} data-testid={`form-responses-${f.id}`} title="View responses"
                      className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 hover:bg-slate-100 transition">
                      <Eye className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
                {!mine && (
                  <button onClick={() => { setPreviewAnswers({}); setPreview(true); setEditor({ ...f, _readonly: true }); }} data-testid={`form-preview-${f.id}`} title="Preview"
                    className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 hover:bg-slate-100 transition">
                    <LayoutTemplate className="h-3.5 w-3.5" />
                  </button>
                )}
                {mine && (
                  <>
                    <button onClick={() => openEditor({ ...f })} data-testid={`form-edit-${f.id}`} title="Edit this form (creates a new version)"
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 transition">
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </button>
                    <button onClick={() => deleteForm(f)} data-testid={`form-delete-${f.id}`} title="Delete"
                      className="inline-flex items-center rounded-lg border border-red-200 bg-white px-2.5 py-2 text-xs text-red-600 hover:bg-red-50 transition">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Builder dialog */}
      <Dialog open={!!editor} onOpenChange={(o) => { if (!o) setEditor(null); }}>
        <DialogContent className="sm:max-w-5xl h-[90vh] flex flex-col overflow-hidden" data-testid="form-builder-dialog">
          <DialogHeader className="shrink-0">
            <DialogTitle className="font-display">{editor?._readonly ? "Form preview" : editor?.id ? "Edit form" : "New form"}</DialogTitle>
            <DialogDescription>Parents fill these fields and e-sign with their full name.</DialogDescription>
          </DialogHeader>
          {editor && (
            <div className="flex-1 min-h-0 flex flex-col gap-3">
              {!editor._readonly && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 shrink-0">
                  <input value={editor.title} onChange={(e) => setEditor({ ...editor, title: e.target.value })}
                    placeholder="Form title — e.g. Field Trip Permission" data-testid="form-title-input" className={inputCls} />
                  <input value={editor.description} onChange={(e) => setEditor({ ...editor, description: e.target.value })}
                    placeholder="Description / instructions (optional)" data-testid="form-desc-input" className={`${inputCls} sm:col-span-2`} />
                </div>
              )}
              {!editor._readonly && (
                <div className="shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid="form-attachments">
                  <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Attachments parents can view (optional)</div>
                  <FileUploader client={api} uploadPath="/forms/files" files={editor.attachments || []} onChange={(attachments) => setEditor({ ...editor, attachments })} label="Attach PDF or picture" testid="form-attachment-uploader" />
                </div>
              )}
              {!editor._readonly && (
                <div className="shrink-0 rounded-lg border border-indigo-200 bg-indigo-50/40 px-3 py-2 flex flex-wrap items-center gap-3" data-testid="form-second-signer">
                  <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800 cursor-pointer">
                    <input type="checkbox" checked={!!editor.second_signer} onChange={(e) => setEditor({ ...editor, second_signer: e.target.checked ? "parent" : "" })} data-testid="form-two-sig-toggle" className="h-4 w-4" />
                    <PenLine className="h-4 w-4 text-indigo-600" /> Require 2 signatures
                  </label>
                  {editor.second_signer && (
                    <>
                      <select value={editor.second_signer} onChange={(e) => setEditor({ ...editor, second_signer: e.target.value })} data-testid="form-second-signer-select" className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm outline-none">
                        {SECOND_SIGNER_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                      </select>
                      <span className="text-[11px] text-slate-500">{SECOND_SIGNER_OPTIONS.find((o) => o.v === editor.second_signer)?.hint}</span>
                    </>
                  )}
                </div>
              )}
              <div className="flex items-center gap-1 shrink-0" data-testid="builder-mode-switch">
                {!editor._readonly && (
                  <button type="button" onClick={() => setPreview(false)} data-testid="builder-mode-edit"
                    className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${!preview ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Layout builder</button>
                )}
                <button type="button" onClick={() => setPreview(true)} data-testid="builder-mode-preview"
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${preview ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Parent preview</button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto pr-1">
                {preview ? (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-6 max-w-2xl mx-auto" data-testid="form-preview-pane">
                    <div className="font-display font-bold text-lg text-slate-900">{editor.title || "Untitled form"}</div>
                    {editor.description && <p className="text-sm text-slate-600 mt-1 mb-4">{editor.description}</p>}
                    {(editor.attachments || []).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5" data-testid="preview-attachments">{editor.attachments.map((a) => <FileChip key={a.id} file={a} client={api} />)}</div>
                    )}
                    <div className="mt-4"><FormRenderer fields={editor.fields} answers={previewAnswers} onChange={(id, v) => setPreviewAnswers({ ...previewAnswers, [id]: v })} testPrefix="preview-field" /></div>
                    <div className="mt-5 rounded-xl border-2 border-dashed border-indigo-200 bg-indigo-50/50 p-3 text-xs text-slate-500">E-signature box appears here for the parent.{editor.second_signer ? ` A second signature (${SECOND_SIGNER_LABEL[editor.second_signer]}) is collected afterwards.` : ""}</div>
                  </div>
                ) : (
                  <FormBuilder fields={editor.fields} onChange={(fields) => setEditor({ ...editor, fields })} />
                )}
              </div>
            </div>
          )}
          {editor && !editor._readonly && (
            <DialogFooter className="shrink-0">
              <button onClick={saveForm} disabled={saving} data-testid="form-save-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save form
              </button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {sendFor && <SendDialog form={sendFor} onClose={() => setSendFor(null)} onSent={load} />}

      {/* Responses dialog */}
      <Dialog open={!!respFor} onOpenChange={(o) => { if (!o) setRespFor(null); }}>
        <DialogContent className="sm:max-w-2xl h-[85vh] flex flex-col overflow-hidden" data-testid="form-responses-dialog">
          <DialogHeader className="shrink-0">
            <DialogTitle className="font-display">Responses — {respFor?.title}</DialogTitle>
            <DialogDescription>{responses.filter((r) => r.status === "completed").length} signed · {responses.filter((r) => r.status === "awaiting_second").length} awaiting 2nd signature · {responses.filter((r) => r.status === "pending").length} pending</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 flex-1 min-h-0 overflow-y-auto pr-1">
            {responses.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">Not sent to anyone yet.</div>}
            {responses.map((r) => (
              <div key={r.id} className="rounded-lg border border-slate-200 p-3" data-testid={`response-${r.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-slate-900">{r.student_name} <span className="font-normal text-slate-500">· parent {r.parent_name}</span></div>
                  <div className="flex items-center gap-1.5">
                    <FormSendActions send={r} onChanged={async () => { const { data } = await api.get(`/forms/${respFor.id}/responses`); setResponses(data); load(); }} />
                    {r.status === "awaiting_second" && r.second_signer === "staff" && (
                      <button onClick={() => { setCounterName(user?.name || ""); setCounterFor(r); }} data-testid={`response-countersign-${r.id}`}
                        className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-indigo-700 transition">
                        <PenLine className="h-3 w-3" /> Countersign
                      </button>
                    )}
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${SEND_STATUS(r)[2]}`} data-testid={`response-status-${r.id}`}>
                      {SEND_STATUS(r)[0]}
                    </span>
                  </div>
                </div>
                <div className="text-[11px] text-slate-400">Sent {new Date(r.sent_at).toLocaleDateString()} by {r.sent_by}{r.second_signer ? ` · 2nd signer: ${SECOND_SIGNER_LABEL[r.second_signer]}` : ""}{r.revision ? ` · revision ${r.revision}` : ""}</div>
                {r.correction_note && r.status === "pending" && <div className="mt-1 rounded-md bg-orange-50 border border-orange-200 px-2 py-1 text-[11px] text-orange-800" data-testid={`response-note-${r.id}`}>Returned by {r.returned_by}: {r.correction_note}</div>}
                {r.review_status === "approved" && <div className="mt-1 text-[11px] text-emerald-700" data-testid={`response-approved-${r.id}`}>Approved by {r.reviewed_by} · {new Date(r.reviewed_at).toLocaleDateString()}</div>}
                {r.status !== "pending" && (
                  <div className="mt-2 space-y-1 text-xs text-slate-700">
                    {Object.values(r.answers || {}).map((a, i) => (
                      a.type === "file" ? (
                        <div key={i} className="flex flex-wrap items-center gap-1.5"><span className="text-slate-500">{a.label}:</span>
                          {(a.value || []).length === 0 ? <b>—</b> : a.value.map((f) => <FileChip key={f.id} file={f} client={api} testid={`response-file-${f.id}`} />)}
                        </div>
                      ) : (
                        <div key={i}><span className="text-slate-500">{a.label}:</span> <b>{a.type === "checkbox" ? (a.value ? "Yes" : "No") : Array.isArray(a.value) ? a.value.join(", ") || "—" : String(a.value || "—")}</b></div>
                      )
                    ))}
                    <div className="pt-1 text-slate-500 italic">Signed “{r.signature_name}” · {new Date(r.signed_at).toLocaleString()}</div>
                    {r.second_signature_name && <div className="text-slate-500 italic" data-testid={`response-second-sig-${r.id}`}>Countersigned “{r.second_signature_name}” ({r.second_signer_name}) · {new Date(r.second_signed_at).toLocaleString()}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!counterFor} onOpenChange={(o) => { if (!o) setCounterFor(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="countersign-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><PenLine className="h-5 w-5 text-indigo-600" /> Countersign for {counterFor?.student_name}</DialogTitle>
            <DialogDescription>Parent {counterFor?.parent_name} signed “{counterFor?.signature_name}”. Type your full legal name to add the school's signature.</DialogDescription>
          </DialogHeader>
          <input value={counterName} onChange={(e) => setCounterName(e.target.value)} placeholder="Your full name" data-testid="countersign-name"
            className="w-full h-12 rounded-lg border border-indigo-200 bg-white px-3 text-lg italic font-serif outline-none focus:border-indigo-400" />
          <DialogFooter>
            <button onClick={countersign} disabled={countersigning} data-testid="countersign-confirm" className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
              {countersigning ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Countersign
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

function SendDialog({ form, onClose, onSent }) {
  const { user } = useAuth();
  const isCorp = user?.role === "corporate_admin";
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [students, setStudents] = useState([]);
  const [picked, setPicked] = useState([]);
  const [q, setQ] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (isCorp) api.get("/corporate/transfer-options").then(({ data }) => setSchools(data)).catch(() => {});
    else api.get("/classes").then(({ data }) => setClasses(data)).catch(() => {});
  }, [isCorp]);
  useEffect(() => {
    if (!isCorp) return;
    setClassId("");
    setClasses(schools.find((s) => s.id === schoolId)?.grades || []);
  }, [isCorp, schoolId, schools]);
  useEffect(() => {
    setPicked([]);
    api.get("/forms/recipients", { params: { class_id: classId, school_id: schoolId } }).then(({ data }) => setStudents(data)).catch(() => setStudents([]));
  }, [classId, schoolId]);

  const shown = students.filter((s) => !q || s.full_name.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const reachable = (picked.length ? students.filter((s) => picked.includes(s.id)) : students).filter((s) => s.has_parent).length;

  const doSend = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/forms/${form.id}/send`, { class_id: classId, student_ids: picked, school_id: schoolId });
      if (data.sent === 0) toast.warning("No new recipients — parents may not be activated yet, or they already have this form pending.");
      else toast.success(`Sent to ${data.sent} parent${data.sent === 1 ? "" : "s"}${data.schools > 1 ? ` across ${data.schools} schools` : ""}${data.emailed ? ` · ${data.emailed} emailed` : ""}`);
      if (data.single_guardian) toast.warning(`${data.single_guardian} student${data.single_guardian === 1 ? " has" : "s have"} only one guardian in the app — invite the second guardian so they can countersign.`);
      onSent();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed"); }
    finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg h-[80vh] flex flex-col overflow-hidden" data-testid="form-send-dialog">
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-display">Send "{form.title}"</DialogTitle>
          <DialogDescription>Parents get it in the parent app and by email, and must fill + e-sign it.</DialogDescription>
        </DialogHeader>
        {isCorp && (
          <label className="block shrink-0">
            <span className="block mb-1 text-[11px] font-medium uppercase tracking-widest text-slate-500">School</span>
            <select value={schoolId} onChange={(e) => setSchoolId(e.target.value)} data-testid="form-send-school" className={inputCls}>
              <option value="">All schools in the district ({schools.length})</option>
              {schools.map((sc) => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
            </select>
          </label>
        )}
        {(!isCorp || schoolId) && (
          <label className="block shrink-0">
            <span className="block mb-1 text-[11px] font-medium uppercase tracking-widest text-slate-500">Grade / class</span>
            <select value={classId} onChange={(e) => setClassId(e.target.value)} data-testid="form-send-grade" className={inputCls}>
              <option value="">{classes.length ? "All students" : "All students"}</option>
              {classes.map((g) => <option key={g.id} value={g.id}>{`Grade: ${g.name}`}</option>)}
            </select>
          </label>
        )}
        <div className="shrink-0 flex items-center justify-between gap-2">
          <span className="text-[11px] font-medium uppercase tracking-widest text-slate-500 inline-flex items-center gap-1"><Users className="h-3 w-3" /> Specific students (optional)</span>
          {picked.length > 0 && <button type="button" onClick={() => setPicked([])} data-testid="form-send-clear" className="text-[11px] text-primary hover:underline">Clear ({picked.length})</button>}
        </div>
        <div className="relative shrink-0">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search students…" data-testid="form-send-search" className={`${inputCls} h-9 pl-8`} />
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="form-send-students">
          {shown.length === 0 && <div className="p-4 text-center text-xs text-slate-400">No students.</div>}
          {shown.map((s) => (
            <label key={s.id} className="flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`form-send-student-${s.id}`}>
              <input type="checkbox" checked={picked.includes(s.id)} onChange={() => toggle(s.id)} className="h-4 w-4" />
              <span className="flex-1 truncate text-slate-900">{s.full_name}</span>
              <span className="text-[11px] text-slate-400">{isCorp && !schoolId ? `${s.school_name}${s.class_name ? ` · ${s.class_name}` : ""}` : (s.class_name || s.grade)}</span>
              {!s.has_parent && <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700" title="No parent has joined the app yet">no parent app</span>}
            </label>
          ))}
        </div>
        <DialogFooter className="shrink-0 sm:justify-between sm:items-center">
          <span className="text-xs text-slate-500" data-testid="form-send-summary">
            {picked.length ? `${picked.length} selected` : `All ${students.length} students`} · {reachable} with parent app
          </span>
          <button onClick={doSend} disabled={sending || students.length === 0} data-testid="form-send-confirm"
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send now
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
